/**
 * Push notification registration helper — Sprint 16.
 * Called from App.tsx on login to register/renew push subscription.
 *
 * Uses the existing /api/notifications/subscribe endpoint and
 * /api/notifications/vapid-key for the VAPID public key.
 */

import { apiFetch, ApiError } from './api';

/**
 * Registers for push notifications if the browser supports them
 * and the user hasn't already subscribed from this device.
 */
export async function registerPushNotifications(): Promise<void> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return; // Browser doesn't support push
  }

  try {
    const registration = await navigator.serviceWorker.ready;

    // Get VAPID public key from server
    const { vapid_public_key } = await apiFetch<{ vapid_public_key: string }>('/api/notifications/vapid-key');
    if (!vapid_public_key) return;

    // Check existing subscription
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      // Subscribe
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid_public_key) as BufferSource,
      });
    }

    // Send to server
    const p256dh = subscription.getKey('p256dh');
    const auth = subscription.getKey('auth');

    if (!p256dh || !auth) return;

    await apiFetch('/api/notifications/subscribe', {
      method: 'POST',
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(p256dh),
          auth: arrayBufferToBase64(auth),
        },
      }),
    });
  } catch (err) {
    // Push not configured on server (VAPID key missing) — silently ignore
    if (err instanceof ApiError && (err.status === 503 || err.code === 'NOT_CONFIGURED')) return;
    // Unexpected failure — log for debugging
    console.warn('[Push] Registration failed:', err);
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}
